import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Logic Trail — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 *
 * Lit le state via `adapter.subscribe` (`useSyncExternalStore`).
 * Dispatche les inputs user via `adapter.handleNodeTap`.
 *
 * Pour le debrief, lit `snapshot.staircase` via `useGameShellFromContext` et
 * compose le rendu via `<DebriefShell>`.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
const STAGE_SIZE = 280;
const NODE_RADIUS = 14;
function Stage({ stim, expectedSeqIdx, onTap, feedback }) {
    const visited = stim.nodes.slice(0, expectedSeqIdx);
    // Construction des segments deja relies (correct path).
    const segments = [];
    for (let i = 0; i < visited.length - 1; i += 1) {
        const a = visited[i];
        const b = visited[i + 1];
        segments.push({
            x1: a.x * STAGE_SIZE,
            y1: a.y * STAGE_SIZE,
            x2: b.x * STAGE_SIZE,
            y2: b.y * STAGE_SIZE,
        });
    }
    const expected = stim.nodes[expectedSeqIdx];
    return (_jsxs("div", { className: "relative", style: {
            width: STAGE_SIZE,
            height: STAGE_SIZE,
            background: '#1a1a1a',
        }, children: [_jsx("svg", { width: STAGE_SIZE, height: STAGE_SIZE, className: "absolute inset-0", "aria-hidden": true, children: segments.map((s, i) => (_jsx("line", { x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, stroke: "#7d9b6a", strokeWidth: 3, strokeLinecap: "round" }, i))) }), stim.nodes.map((node) => {
                const cx = node.x * STAGE_SIZE;
                const cy = node.y * STAGE_SIZE;
                const isVisited = node.seqIdx < expectedSeqIdx;
                const isNext = expected && node.id === expected.id;
                let bg = '#2b2b2b';
                let border = '#3d3d3d';
                let textCls = 'text-cyan-surface';
                if (feedback === 'correct' && isVisited) {
                    bg = '#5b7a4a';
                    border = '#7d9b6a';
                }
                else if (feedback === 'incorrect' && isVisited) {
                    bg = '#7a3d3d';
                    border = '#a85555';
                }
                else if (isVisited) {
                    bg = '#3a4d2f';
                    border = '#7d9b6a';
                }
                else if (isNext) {
                    bg = '#2b2b2b';
                    border = '#bcd8a3';
                    textCls = 'text-cyan-surface';
                }
                return (_jsx("button", { type: "button", onClick: () => onTap(node.id), "aria-label": node.label, className: [
                        'absolute flex items-center justify-center rounded-full border-2 font-display text-[14px] transition duration-feedback ease-soft-wind active:scale-95',
                        textCls,
                        isNext ? 'animate-pulse' : '',
                    ].join(' '), style: {
                        width: NODE_RADIUS * 2,
                        height: NODE_RADIUS * 2,
                        left: cx - NODE_RADIUS,
                        top: cy - NODE_RADIUS,
                        background: bg,
                        borderColor: border,
                    }, children: node.label }, node.id));
            })] }));
}
function FixationCross() {
    return (_jsx("div", { className: "flex items-center justify-center", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: _jsx("span", { className: "select-none font-display text-[44px] text-cyan-surface", children: "+" }) }));
}
function formatSeconds(ms) {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `${s}s`;
}
export function LTStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleTap = useCallback((id) => adapter.handleNodeTap(id), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const expectedLabel = adapterState.stim?.nodes[adapterState.expectedSeqIdx]?.label ?? '—';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex w-full items-center justify-between px-s2 text-[11px] text-cyan-text-secondary", children: [_jsxs("span", { children: [t('logic_trail.hud.expected_label'), " :", ' ', _jsx("span", { className: "font-mono text-cyan-network", children: expectedLabel })] }), _jsxs("span", { children: [t('logic_trail.hud.errors'), " :", ' ', _jsx("span", { className: "font-mono", children: adapterState.errors })] }), _jsxs("span", { children: [t('logic_trail.hud.time_left'), " :", ' ', _jsx("span", { className: "font-mono", children: formatSeconds(adapterState.remainingMs) })] })] }), _jsxs("div", { className: "relative", children: [adapterState.subPhase === 'fixation' && _jsx(FixationCross, {}), adapterState.subPhase === 'trail' && adapterState.stim && (_jsx(Stage, { stim: adapterState.stim, expectedSeqIdx: adapterState.expectedSeqIdx, onTap: handleTap, feedback: null })), adapterState.subPhase === 'feedback' && adapterState.stim && (_jsxs("div", { className: "flex flex-col items-center justify-center gap-s2", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: [_jsx("p", { className: [
                                    'font-display text-[40px]',
                                    adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: adapterState.feedback === 'correct' ? '✓' : '✗' }), _jsx("p", { className: "px-s4 text-center text-[12px] text-cyan-surface", children: adapterState.feedback === 'correct'
                                    ? t('logic_trail.feedback.correct_with_time', {
                                        time: formatSeconds(adapterState.stim.deadlineMs - adapterState.remainingMs),
                                    })
                                    : t('logic_trail.feedback.incorrect_errors', {
                                        errors: adapterState.errors,
                                    }) })] })), (adapterState.subPhase === 'iti' || adapterState.subPhase === 'idle') && (_jsx("div", { style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, "aria-hidden": true }))] })] }));
}
function recommendKey(threshold) {
    if (threshold !== null && threshold >= 18) {
        return 'logic_trail.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold >= 12) {
        return 'logic_trail.debrief.recommend_good';
    }
    return 'logic_trail.debrief.recommend_keep';
}
export function LTDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestN = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : Math.round(threshold).toString();
    const replay = () => window.location.reload();
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('logic_trail.debrief.title'), scoreLabel: t('logic_trail.debrief.score_label'), scoreValue: thresholdLabel, equivalentAge: t('logic_trail.accuracy_global', { accuracy }), fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('logic_trail.debrief.metric_best_n'), value: bestN },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('logic_trail.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map